/**
 * roles.js — Source unique des rôles et droits de la V2 Esprit Padel OS.
 *
 * Remplace les contrôles éparpillés (`admin` booléen, `role` texte libre,
 * `isAdmin`/`isDirector` recalculés localement). Le rôle canonique est DÉDUIT
 * des données existantes — aucune migration nécessaire.
 *
 * Rôles canoniques :
 *   admin      — Responsable comm (Mélissa) : tous les clubs, tout gérer, attribuer.
 *   comm       — Référent comm d'un club : voir + produire + attribuer dans SON club.
 *   directeur  — Directeur de club : voir + déposer + suivre (son club), n'attribue pas.
 *   ja         — Juge-arbitre : déposer (visuels) + suivre (son club), n'attribue pas.
 *
 * Périmètre : admin = tous les clubs ; les autres = leur(s) club(s) (user.clubs).
 */

export const ROLE_LABELS = {
  admin: "Responsable comm",
  comm: "Référent comm",
  directeur: "Directeur",
  ja: "Juge-arbitre",
  guest: "Invité",
};

/** Les 4 rôles canoniques assignables (hors `guest`). */
export const ASSIGNABLE_ROLES = ["admin", "comm", "directeur", "ja"];

/**
 * Rôle canonique d'un utilisateur.
 * Priorité au champ explicite `roleComm` (nouveau, édité depuis la page Équipe) ;
 * à défaut, déduction rétro-compatible depuis `admin` (booléen) + `role` (texte).
 */
export function roleOf(user) {
  if (!user) return "guest";
  const explicit = String(user.roleComm || "").toLowerCase();
  if (ASSIGNABLE_ROLES.includes(explicit)) return explicit;
  if (user.admin === true) return "admin";
  const r = String(user.role || "").toLowerCase();
  if (r.includes("directeur")) return "directeur";
  if (r === "ja" || r.includes("juge")) return "ja";
  // "Alternant communication", "Responsable Communication" (non-admin), etc.
  return "comm";
}

export function isAdmin(user) {
  return roleOf(user) === "admin";
}

/** Ids de clubs auxquels l'utilisateur est rattaché (vide pour admin = tous). */
export function scopeClubs(user) {
  return Array.isArray(user && user.clubs) ? user.clubs.map(String) : [];
}

/** Ids de clubs d'une demande/tâche (gère `clubs[]` + `club` singulier). */
function clubIdsOf(entity) {
  const arr = Array.isArray(entity && entity.clubs) ? entity.clubs.map(String) : [];
  if (entity && entity.club != null && entity.club !== "" && !arr.includes(String(entity.club))) {
    arr.push(String(entity.club));
  }
  return arr;
}

function meId(user) {
  const v = user && (user.appId != null ? user.appId : user.id);
  return v == null ? "" : String(v);
}

/** L'utilisateur voit-il TOUS les clubs ? (admin uniquement) */
export function canViewAllClubs(user) {
  return isAdmin(user);
}

/**
 * L'utilisateur peut-il voir cette demande ?
 * Admin : tout. Sinon : demande de son club, OU demande qui lui est assignée
 * (une demande adressée directement reste visible même hors de son club).
 */
export function canViewRequest(user, r) {
  if (isAdmin(user)) return true;
  if (!r) return false;
  const me = meId(user);
  if (me) {
    const assigned = (Array.isArray(r.assignedTo) ? r.assignedTo.map(String) : [])
      .concat(r.assigneeId != null ? [String(r.assigneeId)] : []);
    if (assigned.includes(me)) return true;
  }
  const scope = scopeClubs(user);
  return clubIdsOf(r).some((id) => scope.includes(id));
}

/**
 * L'utilisateur peut-il attribuer cette demande ?
 * Admin : partout. Référent comm : seulement dans son club (demande rattachée
 * à un de ses clubs). Directeur / JA : jamais.
 */
export function canAttribute(user, r) {
  if (isAdmin(user)) return true;
  if (roleOf(user) !== "comm") return false;
  const scope = scopeClubs(user);
  const rc = clubIdsOf(r);
  return rc.length > 0 && rc.some((id) => scope.includes(id));
}

/** Gérer (créer/éditer/supprimer campagnes, objectifs, réglages) : admin. */
export function canManage(user) {
  return isAdmin(user);
}

/** Produire (travailler tâches, campagnes, contenus) : admin + référent comm. */
export function canProduce(user) {
  const role = roleOf(user);
  return role === "admin" || role === "comm";
}

/** Déposer une demande : tout utilisateur connecté. */
export function canSubmit(user) {
  return roleOf(user) !== "guest";
}
